// Admin › Returns › Errors: what went wrong in the BargainFox returns portal.
// Portal-BE records every failure (customer site crashes, label bookings,
// refunds, emails that could not be sent, ...) as a return_events row with
// event 'error'; GET /admin/returns/errors lists this BE's own environment
// (staging, production and laptops share one database).
//
// The filters ride the URL (?tab=errors&area=&days=&seen=&page=) so the hourly
// alert email can link straight here. The list shows only NEW errors, the ones
// nobody has marked seen yet; "Show seen" (seen=all) brings the seen ones back,
// muted. Marking errors seen (one row, or every new one of the period and area)
// is the only change made on this tab, and needs admin.returns write; an error
// that happens again after it was marked comes back new by itself (the BE does it).
// A row tied to a request opens it in the request list's side panel (?ref=).
import { Fragment, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Check, CheckCheck, ChevronDown, ChevronLeft, ChevronRight, ChevronUp, Mail, RefreshCw } from "lucide-react";
import { useTranslation } from "../../i18n";
import {
    RETURN_ERROR_AREAS, ReturnErrorRow, ReturnErrorsPage, ReturnErrorsSeen, MarkReturnErrorsSeenBody,
    getReturnErrors, markReturnErrorsSeen,
} from "../../api/returns";

type T = (key: string, params?: Record<string, string>) => string;

/** The periods offered, in days. */
export const ERROR_PERIODS = [1, 7, 30] as const;
export const DEFAULT_ERROR_DAYS = 7;
const PAGE_SIZE = 50;
const SETTINGS_LINK = "/admin/settings#returns-emails";

const KNOWN_AREAS: readonly string[] = RETURN_ERROR_AREAS;

/** "labels" → "Labels"; an area this FE doesn't know shows as it came. */
export const errorAreaLabel = (t: T, area: string) => (KNOWN_AREAS.includes(area) ? t(`rt_err_area_${area}`) : area);

/** The tab badge: errors not yet marked seen in the last 7 days. A BE from before
 *  marking existed has no `unseen`; its last-24-hours count stands in. */
export const unseenErrorCount = (p: ReturnErrorsPage) => p.counts.unseen ?? p.counts.last_24h;

// The detail lines, in this order (the stack has its own block); keys the BE adds later show by name.
const DETAIL_ORDER = ["kind", "code", "status", "api_path", "page", "build", "instance", "user_agent"];
const filled = (v: unknown) => v !== undefined && v !== null && v !== "";
const detailLines = (detail: ReturnErrorRow["detail"]) => {
    const keys = Object.keys(detail ?? {}).filter((k) => k !== "stack" && filled(detail[k]));
    const known = DETAIL_ORDER.filter((k) => keys.includes(k));
    const other = keys.filter((k) => !DETAIL_ORDER.includes(k)).sort();
    return [...known, ...other].map((k) => ({ key: k, value: typeof detail[k] === "object" ? JSON.stringify(detail[k]) : String(detail[k]) }));
};

interface Props {
    /** "" = every area. */
    area: string;
    days: number;
    /** "new" = only errors not yet marked seen (the default); "all" = seen ones too. */
    seen: ReturnErrorsSeen;
    page: number;
    reloadKey: number;
    /** May mark errors seen (admin.returns write). */
    canWrite: boolean;
    /** The request open in the side panel, to mark its rows. */
    selectedRef: string | null;
    /** Change the URL's filters (null removes one). */
    onFilter: (patch: Record<string, string | null>) => void;
    onOpenRequest: (reference: string) => void;
    onRefresh: () => void;
    /** The errors not yet marked seen (last 7 days), from each load and each mark, so the tab badge agrees. */
    onCounts: (unseen: number) => void;
}

const ReturnErrors = ({ area, days, seen, page, reloadKey, canWrite, selectedRef, onFilter, onOpenRequest, onRefresh, onCounts }: Props) => {
    const { t, language } = useTranslation();
    const [data, setData] = useState<ReturnErrorsPage | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [open, setOpen] = useState<Record<number, boolean>>({});
    // Marking: the row (or "all") being sent, a failure to show above the list, and a reload of just this list.
    const [busy, setBusy] = useState<number | "all" | null>(null);
    const [markError, setMarkError] = useState<string | null>(null);
    const [listKey, setListKey] = useState(0);
    const showSeen = seen === "all";

    // One formatter per language, never one per row (Intl objects leak native memory).
    const when = useMemo(() => new Intl.DateTimeFormat(language === "pl" ? "pl-PL" : "en-GB", {
        day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", second: "2-digit",
    }), [language]);

    useEffect(() => {
        let alive = true;
        setLoading(true);
        getReturnErrors({ days, area: area || undefined, seen: showSeen ? "all" : undefined, page, page_size: PAGE_SIZE })
            .then((r) => {
                if (!alive) return;
                if (r.ok && r.data) {
                    setData(r.data);
                    setError(null);
                    onCounts(unseenErrorCount(r.data));
                } else {
                    setError(r.notAvailable ? t("rt_not_available") : r.message || t("rt_err_load_failed"));
                }
            })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [days, area, showSeen, page, reloadKey, listKey]);

    // A failed mark belongs to the list it was made on.
    useEffect(() => { setMarkError(null); }, [days, area, showSeen, page, reloadKey]);

    /** Send a mark, then reload the list (on `nextPage` when the rows marked emptied this one). */
    const mark = async (body: MarkReturnErrorsSeenBody, which: number | "all", nextPage: number) => {
        setBusy(which);
        setMarkError(null);
        const r = await markReturnErrorsSeen(body);
        setBusy(null);
        if (!r.ok || !r.data) {
            setMarkError(r.notAvailable ? t("rt_not_available") : r.message || t("rt_err_mark_failed"));
            return;
        }
        onCounts(r.data.unseen);
        if (nextPage !== page) onFilter({ page: nextPage > 1 ? String(nextPage) : null });
        else setListKey((k) => k + 1);
    };
    // Marked rows leave the new-errors list (seen=all keeps them, muted): the last one on a page steps back a page,
    // and "Mark all" empties every page.
    const markRow = (id: number, asSeen: boolean) => mark(asSeen ? { ids: [id] } : { ids: [id], seen: false }, id,
        !showSeen && data?.rows.length === 1 && page > 1 ? page - 1 : page);
    const markAll = () => mark({ all: true, area: area || undefined, days }, "all", showSeen ? page : 1);

    // Every known area, then any the BE reported (or the URL names) that this FE doesn't know.
    const areas = useMemo(() => {
        const extra = Object.keys(data?.counts.by_area ?? {}).filter((a) => !KNOWN_AREAS.includes(a));
        if (area && !KNOWN_AREAS.includes(area) && !extra.includes(area)) extra.push(area);
        return [...KNOWN_AREAS, ...extra.sort()];
    }, [data, area]);

    const byArea = Object.entries(data?.counts.by_area ?? {}).filter(([, n]) => n > 0).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
    const inPeriod = byArea.reduce((sum, [, n]) => sum + n, 0);

    const total = data?.total ?? 0;
    const firstRow = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
    const lastRow = Math.min(total, page * PAGE_SIZE);
    const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

    // Anything left to mark in this period and area: the new-errors list is all unseen.
    const hasUnseen = !!data && (showSeen ? data.rows.some((r) => !r.seen_at) : total > 0);
    // Nothing new, but the period had errors (seen ones, then): offer to show them. All areas, seen or not.
    const periodHadAny = !!data && (days === 1 ? data.counts.last_24h : data.counts.last_7d) > 0;

    return (
        <section className={`rt-errors${canWrite ? " rt-errors--write" : ""}`} aria-label={t("rt_tab_errors")}>
            <div className="rt-filters">
                <label className="rt-date rt-err-filter">
                    <span>{t("rt_err_area")}</span>
                    <select className="rt-input" value={area} onChange={(e) => onFilter({ area: e.target.value || null, page: null })}>
                        <option value="">{t("rt_err_area_all")}</option>
                        {areas.map((a) => <option key={a} value={a}>{errorAreaLabel(t, a)}</option>)}
                    </select>
                </label>
                <label className="rt-date rt-err-filter">
                    <span>{t("rt_err_period")}</span>
                    <select className="rt-input" value={String(days)}
                        onChange={(e) => onFilter({ days: Number(e.target.value) === DEFAULT_ERROR_DAYS ? null : e.target.value, page: null })}>
                        {ERROR_PERIODS.map((d) => <option key={d} value={String(d)}>{t(`rt_err_period_${d}`)}</option>)}
                    </select>
                </label>
                <label className="rt-err-showseen">
                    <input type="checkbox" checked={showSeen} onChange={(e) => onFilter({ seen: e.target.checked ? "all" : null, page: null })} />
                    <span>{t("rt_err_show_seen")}</span>
                </label>
                <button type="button" className="rt-icon-btn" onClick={onRefresh} aria-label={t("rt_refresh")} title={t("rt_refresh")}>
                    <RefreshCw size={16} className={loading ? "rt-spin" : undefined} />
                </button>
                {canWrite && hasUnseen && (
                    <button type="button" className="btn btn-sm rt-btn btn-secondary" disabled={busy !== null}
                        title={t("rt_err_mark_all_title")} onClick={markAll}>
                        <CheckCheck size={14} aria-hidden="true" /> {t("rt_err_mark_all_seen")}
                    </button>
                )}
            </div>

            {error && <p className="rt-error" role="alert">{error}</p>}
            {markError && <p className="rt-error" role="alert" data-testid="rt-err-mark-error">{markError}</p>}

            {data && (
                <div className="rt-err-summary" data-testid="rt-err-summary">
                    <p>
                        <strong>{t(showSeen ? `rt_err_in_${days}` : `rt_err_new_in_${days}`, { n: String(inPeriod) })}</strong>
                        {byArea.map(([a, n]) => (
                            <button key={a} type="button" className="rt-chip" aria-pressed={area === a}
                                onClick={() => onFilter({ area: area === a ? null : a, page: null })}>
                                {errorAreaLabel(t, a)} <strong>{n}</strong>
                            </button>
                        ))}
                    </p>
                    <p className="rt-muted rt-err-alertline" data-testid="rt-err-alerts">
                        <Mail size={14} aria-hidden="true" />
                        <span>{data.alert_email ? t("rt_err_alerts_to", { email: data.alert_email }) : t("rt_err_no_alerts")}</span>
                        <Link to={SETTINGS_LINK} className="rt-err-link">{data.alert_email ? t("rt_err_alerts_change") : t("rt_err_alerts_add")}</Link>
                        <span aria-hidden="true">·</span>
                        <span>{t("rt_err_env", { env: data.env })}</span>
                    </p>
                </div>
            )}

            <div className="rt-table" role="table" aria-busy={loading} aria-label={t("rt_tab_errors")}>
                <div className="rt-tr rt-tr--head rt-err-tr" role="row">
                    <span role="columnheader">{t("rt_err_col_time")}</span>
                    <span role="columnheader">{t("rt_err_area")}</span>
                    <span role="columnheader">{t("rt_err_col_message")}</span>
                    <span role="columnheader">{t("rt_col_request")}</span>
                    <span role="columnheader" aria-label={t("rt_err_show_details")} />
                </div>
                {data && data.rows.length === 0 && !loading && (
                    showSeen ? <p className="rt-empty">{t("rt_err_empty")}</p> : (
                        <p className="rt-empty" data-testid="rt-err-empty-new">
                            {t("rt_err_empty_new")}
                            {periodHadAny && (<>
                                {" "}{t("rt_err_empty_seen_hidden")}{" "}
                                <button type="button" className="rt-err-linkbtn" onClick={() => onFilter({ seen: "all", page: null })}>{t("rt_err_show_seen")}</button>
                            </>)}
                        </p>
                    )
                )}
                {data?.rows.map((r) => {
                    const lines = detailLines(r.detail);
                    const expandable = lines.length > 0 || filled(r.detail?.stack);
                    const isOpen = expandable && !!open[r.id];
                    const isSeen = !!r.seen_at;
                    return (
                        <Fragment key={r.id}>
                            <div role="row" data-testid="rt-err-row"
                                className={`rt-tr rt-err-tr${isSeen ? " rt-err-tr--seen" : ""}${r.reference && r.reference === selectedRef ? " rt-tr--on" : ""}`}>
                                <span role="cell" className="rt-err-when">{when.format(new Date(r.created_at))}</span>
                                <span role="cell" className="rt-err-area">
                                    <span className="rt-pill rt-pill--plain">{errorAreaLabel(t, r.area)}</span>
                                    {r.where && <span className="rt-muted rt-ellipsis" title={r.where}>{r.where}</span>}
                                </span>
                                <span role="cell" className="rt-err-msg">
                                    <span className="rt-err-text">
                                        {r.message}
                                        {r.repeats > 0 && (
                                            <span className="rt-pill rt-pill--amber" title={t("rt_err_repeats_title", { n: String(r.repeats + 1) })}>×{r.repeats + 1}</span>
                                        )}
                                    </span>
                                    {r.action && (
                                        <span className="rt-err-action"><AlertTriangle size={13} aria-hidden="true" /> {t("rt_err_action", { action: r.action })}</span>
                                    )}
                                    {isSeen && (
                                        <span className="rt-err-seen" data-testid="rt-err-seen">
                                            <Check size={13} aria-hidden="true" />
                                            <span>{r.seen_by
                                                ? t("rt_err_seen_by", { name: r.seen_by, time: when.format(new Date(r.seen_at!)) })
                                                : t("rt_err_seen_at", { time: when.format(new Date(r.seen_at!)) })}</span>
                                            {canWrite && (
                                                <button type="button" className="rt-err-linkbtn" disabled={busy !== null} onClick={() => markRow(r.id, false)}>
                                                    {t("rt_err_mark_unseen")}
                                                </button>
                                            )}
                                        </span>
                                    )}
                                </span>
                                <span role="cell" className="rt-err-req">
                                    {r.reference && (
                                        <button type="button" className="rt-err-ref rt-mono" onClick={() => onOpenRequest(r.reference!)}>{r.reference}</button>
                                    )}
                                    {r.order_reference && <span className="rt-muted">#{r.order_reference}</span>}
                                    {!r.reference && !r.order_reference && <span className="rt-muted">-</span>}
                                </span>
                                <span role="cell" className="rt-err-toggle">
                                    {canWrite && !isSeen && (
                                        <button type="button" className="btn btn-sm rt-btn btn-secondary rt-err-markseen" disabled={busy !== null}
                                            title={t("rt_err_mark_seen_title")} onClick={() => markRow(r.id, true)}>
                                            <Check size={13} aria-hidden="true" /> {t("rt_err_mark_seen")}
                                        </button>
                                    )}
                                    {expandable && (
                                        <button type="button" className="rt-icon-btn" aria-expanded={isOpen} aria-controls={`rt-err-${r.id}`}
                                            aria-label={isOpen ? t("rt_err_hide_details") : t("rt_err_show_details")}
                                            title={isOpen ? t("rt_err_hide_details") : t("rt_err_show_details")}
                                            onClick={() => setOpen((o) => ({ ...o, [r.id]: !o[r.id] }))}>
                                            {isOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                                        </button>
                                    )}
                                </span>
                            </div>
                            {isOpen && (
                                <div role="row" id={`rt-err-${r.id}`} className="rt-err-detail" data-testid="rt-err-detail">
                                    <div role="cell">
                                        {lines.length > 0 && (
                                            <dl className="rt-kv">
                                                {lines.map(({ key, value }) => (
                                                    <Fragment key={key}>
                                                        <dt>{DETAIL_ORDER.includes(key) ? t(`rt_err_detail_${key}`) : key}</dt>
                                                        <dd className={key === "user_agent" ? undefined : "rt-mono"}>{value}</dd>
                                                    </Fragment>
                                                ))}
                                            </dl>
                                        )}
                                        {filled(r.detail?.stack) && (
                                            <>
                                                <span className="rt-label">{t("rt_err_detail_stack")}</span>
                                                <pre className="rt-err-stack">{String(r.detail.stack)}</pre>
                                            </>
                                        )}
                                    </div>
                                </div>
                            )}
                        </Fragment>
                    );
                })}
            </div>

            <div className="rt-pager">
                <span className="rt-muted">{t("rt_showing", { from: String(firstRow), to: String(lastRow), total: String(total) })}</span>
                <span className="rt-pager-btns">
                    <button type="button" className="btn btn-sm rt-btn btn-ghost" disabled={page <= 1 || loading} onClick={() => onFilter({ page: page - 1 > 1 ? String(page - 1) : null })}>
                        <ChevronLeft size={14} /> {t("rt_prev")}
                    </button>
                    <span className="rt-muted">{t("rt_page_of", { page: String(page), pages: String(pages) })}</span>
                    <button type="button" className="btn btn-sm rt-btn btn-ghost" disabled={page >= pages || loading} onClick={() => onFilter({ page: String(page + 1) })}>
                        {t("rt_next")} <ChevronRight size={14} />
                    </button>
                </span>
            </div>
        </section>
    );
};

export default ReturnErrors;
